"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { Link } from "react-router-dom"
import { ShieldAlert } from "lucide-react"
import type { PlanConstraintConflict, PlanProposalResponse } from "@bitebalance/contracts"
import { ApiError, isDeadPlanPreview } from "@bitebalance/contracts/client"
import { useApiClient } from "@/api/useApiClient"
import { useI18n } from "@/i18n"
import { humanizeRecipeId } from "./recipeDisplay"
import { Button } from "@/components/ui/button"
import { CheckoutConflictNotice, GoalImpactLine, conflictAmounts } from "./BudgetImpactNotices"

/**
 * 2026-09-02 acceptance finding C: when the CURRENT plan contains meals
 * that violate the user's CURRENT hard constraints (a newly added
 * never-eat item, allergen, dietary change), the server lists them in
 * `constraintConflicts` — computed with the exact predicate generation
 * enforces. This banner (danger styling, above every other plan notice)
 * makes those meals visibly NOT-recommended and offers the targeted
 * repair: one server proposal scoped to exactly the conflicting meals,
 * carrying the server-computed exclusion constraints, through the normal
 * preview → approve → apply flow. Budget Model v2 (owner D-2): a repair
 * whose checkout would exceed the hard maximum is BLOCKED with the same
 * notice as any proposal (no consent box, no override) — safety and the
 * maximum are BOTH stated, neither hidden; the user changes the maximum in
 * Settings. All policy is server-side; this component only renders/relays.
 */
export function ConstraintConflictBanner({
  planId,
  baseRevision,
  conflicts,
  onApplied,
}: {
  planId: string
  baseRevision: number
  conflicts: PlanConstraintConflict[]
  onApplied: () => void
}) {
  const { t } = useI18n()
  const api = useApiClient()
  const [phase, setPhase] = useState<
    "idle" | "loading" | "preview" | "applying" | "done" | "error" | "checkoutConflict"
  >("idle")
  const [proposal, setProposal] = useState<PlanProposalResponse | null>(null)
  const [applyConflict, setApplyConflict] = useState<{
    overCents: number | null
    maxCents: number | null
  } | null>(null)

  const excludeIngredientIds = [...new Set(conflicts.flatMap(c => c.excludeIngredientIds))]

  async function requestRepair() {
    setPhase("loading")
    try {
      const result = await api.createPlanProposal(planId, {
        baseRevision,
        scope: { mealIds: conflicts.map(c => c.mealId) },
        intent: "swap",
        constraints: { excludeIngredientIds },
      })
      setProposal(result)
      setPhase("preview")
    } catch {
      setPhase("error")
    }
  }

  async function approveRepair() {
    if (!proposal?.confirmationToken) return
    setPhase("applying")
    try {
      await api.applyPlanProposal(planId, proposal.proposalId, {
        confirmationToken: proposal.confirmationToken,
        baseRevision,
      })
      setPhase("done")
      onApplied()
    } catch (err) {
      if (err instanceof ApiError && err.envelope.code === "MAX_CHECKOUT_CONFLICT") {
        const d = (err.envelope.details ?? {}) as { maxCheckoutCents?: number; checkoutDeltaCents?: number }
        setApplyConflict({ overCents: d.checkoutDeltaCents ?? null, maxCents: d.maxCheckoutCents ?? null })
        setPhase("checkoutConflict")
        return
      }
      if (isDeadPlanPreview(err)) {
        // F14: a newer preview superseded this repair preview (or it expired) — drop it and offer the repair again,
        // never a dead end and never a retry of the same token.
        setProposal(null)
        setPhase("idle")
        return
      }
      setPhase("error")
    }
  }

  return (
    <div role="alert" className="rounded-bb-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-ink">
      <div className="flex gap-3">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-red-900">{t("views.plan.conflictsTitle")}</p>
          <p className="mt-1 text-ink-2">
            {conflicts.length === 1
              ? t("views.plan.conflictsBodyOne")
              : t("views.plan.conflictsBodyOther", { count: conflicts.length })}
          </p>
          {/* Human meal names only — resolved ingredient ids and constraint
              internals never render; they only travel in the repair request. */}
          <ul className="mt-2 list-disc pl-5 text-ink-2">
            {conflicts.map(c => (
              <li key={c.mealId}>
                <span className="font-medium text-ink">{c.title ?? humanizeRecipeId(c.recipeId)}</span>
              </li>
            ))}
          </ul>

          {phase === "idle" && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button type="button" size="sm" className="min-h-11" onClick={() => void requestRepair()}>
                {t("views.plan.conflictsReplaceCta")}
              </Button>
              <Link to="/app/more/constraints" className="text-sm font-medium text-red-800 underline">
                {t("views.plan.conflictsEditCta")}
              </Link>
            </div>
          )}
          {phase === "loading" && <p className="mt-3 text-ink-2">…</p>}

          {phase === "preview" && proposal && (
            <div className="mt-3 rounded-bb-md border border-red-200 bg-paper px-3 py-2.5">
              <p className="font-semibold">{t("views.plan.conflictsPreviewTitle")}</p>
              <ul className="mt-1.5 space-y-1 text-ink-2">
                {proposal.changes.map(change => (
                  <li key={change.mealId}>
                    {change.before ? humanizeRecipeId(change.before.recipeId) : change.mealId}
                    {" → "}
                    <span className="font-medium text-ink">
                      {change.after ? humanizeRecipeId(change.after.recipeId) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
              {proposal.impact?.goalImpact && (
                <div className="mt-2.5">
                  <GoalImpactLine goalImpact={proposal.impact.goalImpact} />
                </div>
              )}
              {proposal.impact?.checkoutConflict === true && (
                <div className="mt-2.5">
                  <CheckoutConflictNotice {...conflictAmounts(proposal.impact)} />
                </div>
              )}
              <Button
                type="button"
                size="sm"
                className="mt-2.5 min-h-11"
                disabled={proposal.impact?.checkoutConflict === true}
                onClick={() => void approveRepair()}
              >
                {t("views.plan.conflictsApproveCta")}
              </Button>
            </div>
          )}

          {phase === "checkoutConflict" && (
            <div className="mt-3">
              <CheckoutConflictNotice
                overCents={applyConflict?.overCents ?? null}
                maxCents={applyConflict?.maxCents ?? null}
              />
            </div>
          )}
          {phase === "applying" && <p className="mt-3 text-ink-2">…</p>}
          {phase === "done" && <p className="mt-3 font-medium text-green-800">{t("views.plan.conflictsApplied")}</p>}
          {phase === "error" && <p className="mt-3 text-red-800">{t("views.plan.conflictsError")}</p>}
        </div>
      </div>
    </div>
  )
}
